<script setup lang="ts">
import { faEllipsisVertical } from '@fortawesome/pro-regular-svg-icons'
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

const props = withDefaults(defineProps<{
  disableMarkAll?: boolean
  disableRemoveAll?: boolean
}>(), {
  disableMarkAll: false,
  disableRemoveAll: false,
})

const emit = defineEmits<{
  (e: 'markAll'): void
  (e: 'removeAll'): void
}>()

const open = ref(false)
const alignRight = ref(false) // flip menu to right-0 when near right edge
const wrapper = ref<HTMLElement | null>(null)
const anchorBtn = ref<HTMLButtonElement | null>(null)
const menuRef = ref<HTMLDivElement | null>(null)

// Distance from the right edge (in the KS-dialog) where we flip to right-0
const EDGE_THRESHOLD = 220 // px

function toggleMenu(e?: MouseEvent) {
  e?.stopPropagation()
  open.value = !open.value
}

function getDialogBody(el: HTMLElement | null) {
  return el?.closest('.ks-dialog-body') as HTMLElement | null
}

function closeMenu() {
  open.value = false
}

function markAll() {
  emit('markAll')
  closeMenu()
}
function removeAll() {
  emit('removeAll')
  closeMenu()
}

function updatePlacement() {
  const body = getDialogBody(wrapper.value)
  const host = wrapper.value
  if (!body || !host) {
    return
  }
  const bodyRect = body.getBoundingClientRect()
  const hostRect = host.getBoundingClientRect()
  const leftWithinBody = hostRect.left - bodyRect.left
  const bodyWidth = bodyRect.width
  alignRight.value = leftWithinBody >= (bodyWidth - EDGE_THRESHOLD)
}

function onDocClick(e: MouseEvent) {
  const t = e.target as Node
  if (!open.value) {
    return
  }
  if (wrapper.value?.contains(t)) {
    return
  }
  closeMenu()
}

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape') {
    closeMenu()
  }
}

onMounted(() => {
  document.addEventListener('mousedown', onDocClick)
  window.addEventListener('keydown', onKeydown)
  window.addEventListener('resize', updatePlacement)
  getDialogBody(wrapper.value)?.addEventListener('scroll', updatePlacement, { passive: true })
})

watch(open, async (o) => {
  if (o) {
    await nextTick()
    updatePlacement()
  }
})

onBeforeUnmount(() => {
  document.removeEventListener('mousedown', onDocClick)
  window.removeEventListener('keydown', onKeydown)
  window.removeEventListener('resize', updatePlacement)
  getDialogBody(wrapper.value)?.removeEventListener('scroll', updatePlacement)
})
</script>

<template>
  <div ref="wrapper" class="relative inline-block text-left">
    <button
      ref="anchorBtn"
      type="button"
      class="inline-flex items-center justify-center w-6 h-6 rounded-[4px] focus:outline-none text-carbon-400 reading-dark:text-carbon-200 transition-colors duration-200"
      :class="{ 'bg-surface-primary hover:bg-surface-primary': open, 'hover:bg-surface-secondary': !open }"
      aria-haspopup="menu"
      :aria-expanded="open"
      @click.stop="toggleMenu"
    >
      <Icon :icon="faEllipsisVertical" size="sm" />
    </button>

    <div
      v-if="open"
      ref="menuRef"
      class="absolute mt-2 z-[50] bg-surface-elevated reading-dark:border reading-dark:border-carbon-400 rounded-md p-1 shadow-md cursor-pointer select-none transition-colors duration-200 min-w-[150px]" :class="[
        alignRight ? 'right-0' : 'left-0',
      ]"
      role="menu"
      tabindex="-1"
    >
      <button
        class="hover:bg-surface-hover text-xs text-left px-3 py-2 focus:bg-blurple-200 reading-dark:focus:bg-carbon-400 rounded-md text-carbon-500 reading-dark:text-carbon-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none whitespace-nowrap w-full"
        role="menuitem"
        :disabled="props.disableMarkAll"
        :aria-disabled="props.disableMarkAll"
        @click="markAll"
      >
        Marker alle underdeler
      </button>
      <button
        class="hover:bg-surface-hover text-xs text-left px-3 py-2 focus:bg-blurple-200 reading-dark:focus:bg-carbon-400 rounded-md text-carbon-500 reading-dark:text-carbon-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none whitespace-nowrap w-full"
        role="menuitem"
        :disabled="props.disableRemoveAll"
        :aria-disabled="props.disableRemoveAll"
        @click="removeAll"
      >
        Fjern alle underdeler
      </button>
    </div>
  </div>
</template>
